'use client'

import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { DataTableColumnHeader } from "@/components/data-table-column-header";
import { CaseAdversary } from "@/api/case";
import { timestamp_to_date } from "@/utils/time";
import { Link } from '@tanstack/react-router';
import LongText from '@/components/long-text';
import { cn } from '@/lib/utils';
import { DataTableRowActions } from "./data-table-row-actions";

export const useAdversaryColumns = (): ColumnDef<CaseAdversary>[] => {
    const { t } = useTranslation();

    return useMemo<ColumnDef<CaseAdversary>[]>(
        () => [
            {
                id: 'client_name',
                accessorKey: 'client.name',
                header: ({ column }) => (
                    <DataTableColumnHeader column={column} title={t('name')} />
                ),
                cell: ({ row }) => {
                    const name = row.original.client?.name;
                    if (!name) return <span className="text-red-500">Invalid Data</span>;
                    return (
                        <LongText className='max-w-36'>{name}</LongText>
                    );
                },
                meta: {
                    className: cn(
                        'drop-shadow-[0_1px_2px_rgb(0_0_0_/_0.1)] dark:drop-shadow-[0_1px_2px_rgb(255_255_255_/_0.1)] lg:drop-shadow-none',
                        'bg-background transition-colors duration-200 group-hover/row:bg-muted group-data-[state=selected]/row:bg-muted',
                        'sticky right md:table-cell'
                    ),
                },
                enableHiding: false,
            },
            {
                id: 'client_type',
                accessorKey: 'client.client_type',
                header: ({ column }) => (
                    <DataTableColumnHeader column={column} title={t('client_type')} />
                ),
                cell: ({ row }) => {
                    const type = row.original.client?.client_type;
                    return (
                        <div className='w-fit text-nowrap'>
                            {type === 'individual' ? t('individual') : t('corporate')}
                        </div>
                    );
                },
            },
            {
                id: 'idnumber',
                header: ({ column }) => (
                    <DataTableColumnHeader column={column} title={t('idnumber')} />
                ),
                cell: ({ row }) => (
                    <div className='w-fit text-nowrap'>{row.original.client?.idnumber || ''}</div>
                ),
            },
            {
                id: 'email',
                header: ({ column }) => (
                    <DataTableColumnHeader column={column} title={t('email')} />
                ),
                cell: ({ row }) => (
                    <div className='w-fit text-nowrap'>{row.original.client?.email}</div>
                ),
            },
            {
                id: 'phone',
                header: ({ column }) => (
                    <DataTableColumnHeader column={column} title={t('phone')} />
                ),
                cell: ({ row }) => (
                    <div dir='ltr' className='w-fit text-nowrap'>{row.original.client?.phone}</div>
                ),
            },
            {
                accessorKey: 'added_by',
                header: ({ column }) => (
                    <DataTableColumnHeader column={column} title={t('added_by')} />
                ),
                cell: ({ row }) => {
                    const addedBy = row.original.added_by;
                    return (
                        <Link
                            to="/users/$id"
                            params={{ id: addedBy.id }}
                            className='w-fit text-nowrap hover:underline'
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        >
                            {addedBy.name}
                        </Link>
                    );
                },
            },
            {
                accessorKey: 'created_at',
                header: ({ column }) => (
                    <DataTableColumnHeader column={column} title={t('created_at')} />
                ),
                cell: ({ row }) => (
                    <div className='w-fit text-nowrap'>
                        {timestamp_to_date(row.original.created_at).toLocaleDateString('en-GB')}
                    </div>
                ),
                enableSorting: true
            },
            {
                id: 'actions',
                cell: ({ row }) => (
                    <DataTableRowActions
                        row={row}
                    />
                ),
            },
        ],
        [t]
    );
};
